import React from 'react';
import { Typography } from '@navi/web-ui/lib/primitives';
import { formatAmount } from '@cp/src/utils/commonUtils';
import HourGlassIcon from '@cp/assets/icons/HourglassIconV2';
import cx from 'classnames';
import dayjs from 'dayjs';
import { EmiFeeActions, IEmiBreakup, PenaltyWaivedLabelMap } from './interfaces';
import { DateFormat } from '@cp/src/utils/DateHelper';

const EmiBreakup: React.FC<IEmiBreakup> = ({ emiData }) => {
  const {
    principal,
    interest,
    totalPenalty,
    paidAmount,
    holdAmount,
    unpaidDueAmount,
    action,
    waivedAmount,
    additionalWaiverInfo,
    waiverOrHoldAmount
  } = emiData || {};
  const { heldTillInDateTimeFormat, refundedAmount = 0 } = additionalWaiverInfo || {};
  const paidAmountGreaterThanZero = paidAmount > 0;
  const penaltyChargesWaivedGreaterThanZero = waiverOrHoldAmount > 0;
  let penaltyWaivedLabel = PenaltyWaivedLabelMap[action || EmiFeeActions.HOLD];
  if (waivedAmount > 0) {
    penaltyWaivedLabel = 'Penalty waived';
  } else if (holdAmount > 0) {
    penaltyWaivedLabel = 'Penalty held';
  }
  const totalDueAmount = principal + interest;

  return (
    <div>
      <div className="flex justify-between mb-4">
        <Typography variant="p3" color="var(--grayscale-2)">
          EMI amount (Principal + Interest)
        </Typography>
        <Typography variant="h4">{formatAmount(totalDueAmount)}</Typography>
      </div>
      <div className="flex justify-between border-b border-dashed border-x-0 border-t-0 border-border pb-4 mb-4">
        <Typography variant="p3" color="var(--grayscale-2)">
          Penalty applied
        </Typography>
        <Typography variant="h4">{formatAmount(totalPenalty)}</Typography>
      </div>
      <div className="flex justify-between mb-4">
        <div>
          <Typography variant="p3" color="var(--grayscale-2)">
            {penaltyWaivedLabel}
          </Typography>
          {heldTillInDateTimeFormat && holdAmount ? (
            <div className="flex items-center mt-1">
              <div className="mr-1">
                <HourGlassIcon />
              </div>
              <Typography variant="p5" color="var(--grayscale-7)">
                (till {dayjs(heldTillInDateTimeFormat).format(DateFormat.HH_mm_a)} today)
              </Typography>
            </div>
          ) : null}
        </div>
        <Typography
          variant="h4"
          className={cx({
            'text-green-base': penaltyChargesWaivedGreaterThanZero
          })}
        >
          {penaltyChargesWaivedGreaterThanZero ? '-' : ''} {formatAmount(waiverOrHoldAmount)}
        </Typography>
      </div>
      <div className="flex justify-between border-b border-dashed border-x-0 border-t-0 border-border pb-4 mb-4">
        <Typography variant="p3" color="var(--grayscale-2)">
          Paid amount
        </Typography>
        <Typography
          variant="h4"
          className={cx({
            'text-green-base': paidAmountGreaterThanZero
          })}
        >
          {paidAmountGreaterThanZero ? '-' : ''} {formatAmount(paidAmount)}
        </Typography>
      </div>
      <div className="flex justify-between">
        <Typography variant="p3" color="var(--grayscale-2)">
          {refundedAmount > 0 ? 'To be refunded' : 'To pay'}
        </Typography>
        <Typography
          variant="h4"
          className={cx({
            'text-red-base': unpaidDueAmount > 0,
            'text-green-base': refundedAmount > 0
          })}
        >
          {formatAmount(refundedAmount > 0 ? refundedAmount : unpaidDueAmount)}
        </Typography>
      </div>
    </div>
  );
};

export default EmiBreakup;
